/**
 * Invoice Table Preview Component
 * Renders a preview of the invoice table with dummy data
 */

import { useTranslation } from 'react-i18next';
import type { TemplateElement } from '../../types/template.types';
import { useCurrencyFormat } from '@/hooks/use-currency-format';

interface InvoiceTablePreviewProps {
  element: TemplateElement;
  style: React.CSSProperties;
}

const DUMMY_ITEMS = [
  {
    position: 1,
    date: '01.11.2024',
    description: 'Beratungsleistung November',
    productType: 'Dienstleistung',
    quantity: '10,00',
    unit: 'Std',
    unitPrice: '120,00',
    amount: '1.200,00'
  },
  {
    position: 2,
    date: '15.11.2024',
    description: 'Projektentwicklung',
    productType: 'Dienstleistung',
    quantity: '5,00',
    unit: 'Std',
    unitPrice: '150,00',
    amount: '750,00'
  },
  {
    position: 3,
    date: '20.11.2024',
    description: 'Technischer Support',
    productType: 'Ware',
    quantity: '3,00',
    unit: 'Std',
    unitPrice: '100,00',
    amount: '300,00'
  }
];

const COLUMN_NAME_MAP: Record<string, string> = {
  'position': 'Pos.',
  'date': 'Datum',
  'itemDate': 'Datum',
  'description': 'Beschreibung',
  'productType': 'Typ',
  'quantity': 'Menge',
  'unit': 'Einheit',
  'unitPrice': 'Einzelpreis',
  'total': 'Betrag'
};

const BASE_COLUMN_WIDTHS: Record<string, number> = {
  'Pos.': 8,
  'Datum': 12,
  'Typ': 12,
  'Beschreibung': 30,
  'Menge': 10,
  'Einheit': 10,
  'Einzelpreis': 15,
  'Betrag': 15
};

const HEADER_LABELS: Record<string, string> = {
  'Pos.': 'Pos.',
  'Datum': 'Datum',
  'Typ': 'Typ',
  'Beschreibung': 'Beschreibung',
  'Menge': 'Menge',
  'Einheit': 'Einheit',
  'Einzelpreis': 'Preis',
  'Betrag': 'Gesamt'
};

export function InvoiceTablePreview({ element, style }: InvoiceTablePreviewProps) {
  const { formatCurrency } = useCurrencyFormat();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tableElement = element as any;
  
  const rawColumns = tableElement.tableColumns || ['Pos.', 'Datum', 'Beschreibung', 'Menge', 'Einheit', 'Einzelpreis', 'Betrag'];
  const tableColumns = rawColumns.map((col: string) => COLUMN_NAME_MAP[col] || col);
  
  const showHorizontalLines = tableElement.tableShowHorizontalLines !== false;
  const borderColor = tableElement.borderColor || '#e5e7eb';
  const headerBgColor = tableElement.headerBackgroundColor || '#f3f4f6';
  const headerTextColor = tableElement.headerTextColor || style.color || '#000000';
  const headerFontWeight = tableElement.headerFontWeight || 'bold';
  
  const visibleColumnsBaseWidth = tableColumns.reduce(
    (sum: number, col: string) => sum + (BASE_COLUMN_WIDTHS[col] || 0), 
    0
  );
  
  const scaleFactor = visibleColumnsBaseWidth > 0 ? 100 / visibleColumnsBaseWidth : 1;
  
  const colWidths: Record<string, string> = {};
  tableColumns.forEach((col: string) => {
    const baseWidth = BASE_COLUMN_WIDTHS[col] || 10;
    colWidths[col] = `${baseWidth * scaleFactor}%`;
  });

  return (
    <div 
      style={{ 
        width: '100%',
        fontSize: style.fontSize,
        fontFamily: style.fontFamily,
      }}
      className="border border-gray-300 rounded overflow-hidden"
    >
      {/* Header Row */}
      <div 
        style={{ 
          backgroundColor: headerBgColor,
          color: headerTextColor,
          borderBottom: showHorizontalLines ? `2px solid ${borderColor}` : 'none',
          fontSize: tableElement.headerFontSize || style.fontSize,
          fontWeight: headerFontWeight
        }}
        className="flex"
      >
        {tableColumns.map((col: string) => (
          <div
            key={col}
            style={{ 
              width: colWidths[col],
              textAlign: col === 'Menge' || col === 'Einzelpreis' || col === 'Betrag' ? 'right' : 
                        col === 'Einheit' ? 'center' : 'left',
              fontSize: tableElement.headerFontSize || style.fontSize
            }}
            className="px-2 py-1.5"
          >
            {HEADER_LABELS[col]}
          </div>
        ))}
      </div>

      {/* Data Rows */}
      {DUMMY_ITEMS.map((item, index) => (
        <div
          key={index}
          style={{ 
            borderBottom: showHorizontalLines ? `1px solid ${borderColor}` : 'none',
            color: style.color
          }}
          className="flex"
        >
          {tableColumns.map((col: string) => {
            let content = '';
            let align: 'left' | 'center' | 'right' = 'left';
            
            if (col === 'Pos.') {
              content = item.position.toString();
            } else if (col === 'Datum') {
              content = item.date;
            } else if (col === 'Typ') {
              content = item.productType;
            } else if (col === 'Beschreibung') {
              content = item.description;
            } else if (col === 'Menge') {
              content = item.quantity;
              align = 'right';
            } else if (col === 'Einheit') {
              content = item.unit;
              align = 'center';
            } else if (col === 'Einzelpreis') {
              content = formatCurrency(parseFloat(item.unitPrice.replace(',', '.')));
              align = 'right';
            } else if (col === 'Betrag') {
              content = formatCurrency(parseFloat(item.amount.replace(',', '.')));
              align = 'right';
            }
            
            return (
              <div
                key={col}
                style={{ 
                  width: colWidths[col],
                  textAlign: align,
                  fontSize: style.fontSize
                }}
                className="px-2 py-1.5"
              >
                {content}
              </div>
            );
          })}
        </div>
      ))}

      {/* Totals Section */}
      <div className="border-t-2" style={{ borderColor: borderColor }}>
        <div 
          className="flex justify-end px-2 py-1" 
          style={{ 
            color: style.color,
            fontSize: tableElement.subtotalFontSize || style.fontSize
          }}
        >
          <div className="w-1/3 text-right">Gesamt netto:</div>
          <div className="w-1/6 text-right font-medium">{formatCurrency(2250)}</div>
        </div>
        <div 
          className="flex justify-end px-2 py-1" 
          style={{ 
            color: style.color,
            fontSize: tableElement.subtotalFontSize || style.fontSize
          }}
        >
          <div className="w-1/3 text-right">Umsatzsteuer (19%):</div>
          <div className="w-1/6 text-right font-medium">{formatCurrency(427.50)}</div>
        </div>
        <div 
          className="flex justify-end px-2 py-2 mt-1"
          style={{ 
            color: tableElement.totalFontColor || style.color,
            fontSize: tableElement.totalFontSize || style.fontSize,
            fontWeight: tableElement.totalFontWeight === 'bold' ? 'bold' : 'normal',
            textDecoration: tableElement.totalUnderline === 'single' ? 'underline' : 
                           tableElement.totalUnderline === 'double' ? 'underline' : 'none',
            textDecorationStyle: tableElement.totalUnderline === 'double' ? 'double' : 'solid'
          }}
        >
          <div className="w-1/3 text-right">Gesamtsumme:</div>
          <div className="w-1/6 text-right font-bold">{formatCurrency(2677.50)}</div>
        </div>
      </div>
    </div>
  );
}
